/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import sortBy from 'lodash/sortBy';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import { createSelector } from 'reselect';

import {
  GET_SUMMARIES,
  GET_SUMMARIES_SUCCESS,
  GET_SUMMARIES_FAILURE,
  GET_SUMMARY,
  GET_SUMMARY_SUCCESS,
  GET_SUMMARY_FAILURE,
  ADD_SUMMARY,
  ADD_SUMMARY_SUCCESS,
  ADD_SUMMARY_FAILURE,
  DELETE_SUMMARY,
  DELETE_SUMMARY_SUCCESS,
  DELETE_SUMMARY_FAILURE,
  UPDATE_SUMMARY,
  UPDATE_SUMMARY_SUCCESS,
  UPDATE_SUMMARY_FAILURE,
  GET_VALID_DATAITEMS_SUMMARY,
  GET_VALID_DATAITEMS_SUMMARY_FAILURE,
  GET_VALID_DATAITEMS_SUMMARY_SUCCESS,
  CLEAR_VALID_DATAITEMS_SUMMARY,
  CLEAR_SUMMARY,
} from 'summaryActionCreators';

// selectors
const selectSummaries = state => get(state, 'summaries.data', {});

export const isDashboardUpdating = state =>
  get(state, 'summaries.updatingData') || get(state, 'monitoring.fetchingData');

// TODO: memoize this better
export const getSummaries = createSelector([selectSummaries], summaries =>
  sortBy(Object.values(summaries), 'sequenceNumber')
);

// TODO memoize this better
export const getEnabledSummaries = state =>
  getSummaries(state).filter(summary => summary.isEnabled);

const getSummaryFromProps = (state, props) =>
  get(state, `summary.data.${get(props, 'match.params.dashboardMetaId')}`);

export const getSummariesFetchingData = state => get(state, 'summaries.fetchingData', false);

export const getSummariesDataError = state => get(state, 'summaries.fetchDataError', null);

export const getSummariesUpdateError = state => get(state, 'summaries.updateDataError', null);

// memoize selector to cache the summary I have to do a deep clone because the layout engine dashboard renderer modifies the data
export const getSummaryByName = createSelector([getSummaryFromProps], summary =>
  cloneDeep(summary)
);

const selectValidDataItemsForSummary = state => get(state, 'validDataItemsForSummary.data');

/**
 * All Valid Data Items For Summary dashboards (memoized)
 */
export const getValidDataItemsForSummary = createSelector(
  [selectValidDataItemsForSummary],
  dataItems => Object.values(dataItems) || []
);

export const summaryReducer = (
  state = { data: {}, updatingData: false, fetchingData: false },
  action
) => {
  switch (action.type) {
    case GET_SUMMARY:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
        updateDataError: null,
      };
    case GET_SUMMARY_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case CLEAR_SUMMARY:
      return { ...state, fetchingData: false, fetchDataError: null, data: {} };
    case GET_SUMMARY_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: {
          [action.payload.dashboardMetaId]: action.payload,
        },
      };
    default:
      return state;
  }
};

export const summariesReducer = (
  state = { data: {}, updatingData: false, fetchingData: false },
  action
) => {
  switch (action.type) {
    case GET_SUMMARIES:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
        updateDataError: null,
      };
    case GET_SUMMARIES_FAILURE:
      return {
        ...state,
        updatingData: false,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case GET_SUMMARIES_SUCCESS:
      return {
        ...state,
        updatingData: false,
        fetchingData: false,
        fetchDataError: null,
        data: keyBy(action.payload, 'dashboardMetaId'),
      };
    case DELETE_SUMMARY_SUCCESS:
      return {
        ...state,
        updatingData: false,
        // remove the deleted summary from the state
        data: omit(state.data, [parseInt(action.payload.dashboardMetaId, 10)]),
      };
    case ADD_SUMMARY_SUCCESS:
    case UPDATE_SUMMARY_SUCCESS:
      return {
        ...state,
        updatingData: false,
        fetchingData: false,
        fetchDataError: null,
        data: { ...state.data, [action.payload.dashboardMetaId]: action.payload },
      };
    case UPDATE_SUMMARY:
    case ADD_SUMMARY:
    case DELETE_SUMMARY:
      return {
        ...state,
        updatingData: action.type,
        updateDataError: null,
      };
    case ADD_SUMMARY_FAILURE:
    case DELETE_SUMMARY_FAILURE:
    case UPDATE_SUMMARY_FAILURE:
      return {
        ...state,
        updatingData: false,
        updateDataError: action.payload,
      };
    default:
      return state;
  }
};

// reducer
export const validDataItemsForSummaryReducer = (
  state = { data: [], fetchingData: false },
  action
) => {
  switch (action.type) {
    case GET_VALID_DATAITEMS_SUMMARY:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case GET_VALID_DATAITEMS_SUMMARY_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case GET_VALID_DATAITEMS_SUMMARY_SUCCESS: {
      // Find the subDataItems and make them children
      const dataItems = action.payload.map(dataItem => {
        const subDataItems = action.payload.filter(
          subDataItem => subDataItem.parentDataItemName === dataItem.name
        );
        return merge({}, dataItem, {
          subDataItems: isEmpty(subDataItems) ? null : subDataItems,
        });
      });

      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: dataItems,
      };
    }
    case CLEAR_VALID_DATAITEMS_SUMMARY: {
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: [],
      };
    }
    default:
      return state;
  }
};
